// frontend/src/pages/TeacherStudentList.jsx (console.log 追加版)

import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  Box,
  CircularProgress,
  Tooltip,
  TableContainer,
  useTheme
} from "@mui/material";

const formatTime = (secs) => {
  if (secs === null || secs === undefined || isNaN(secs)) return "N/A";
  if (secs === 0) return "0s";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s_int = Math.floor(secs % 60);
  return `${h > 0 ? h + "h " : ""}${m > 0 ? m + "m " : ""}${s_int}s`;
};

const ACTIVE_STUDENT_HIGHLIGHT_COLOR = '#1E290B'; 

const truncateTextStyle = {
  maxWidth: '150px', 
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: 'inline-block',
};

export default function TeacherStudentList() {
  const theme = useTheme();
  const [students, setStudents] = useState([]);
  const [orderBy, setOrderBy] = useState("student_full_name");
  const [order, setOrder] = useState("asc");
  const navigate = useNavigate();

  const [loadingStudents, setLoadingStudents] = useState(true);
  const [activeStudentsInfo, setActiveStudentsInfo] = useState({}); 
  const [fetchError, setFetchError] = useState('');

  const fetchActiveStudentData = useCallback(async () => {
    const currentToken = localStorage.getItem("token");
    if (!currentToken) {
      setActiveStudentsInfo({});
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/reports/active-students`, {
          headers: { Authorization: `Bearer ${currentToken}` },
      });
      console.log('API Response for active students:', res.data); // ★デバッグ用ログ追加
      if (res.data && Array.isArray(res.data.active_students)) {
          const newActiveStudentsInfo = {};
          res.data.active_students.forEach(student => {
              newActiveStudentsInfo[String(student.student_id)] = student.currently_watching_title || "情報なし";
          });
          console.log('Processed activeStudentsInfo:', newActiveStudentsInfo); // ★デバッグ用ログ追加
          setActiveStudentsInfo(newActiveStudentsInfo);
      } else {
          setActiveStudentsInfo({});
      }
    } catch (err) {
        console.error("アクティブ学生情報の取得に失敗:", err);
        setActiveStudentsInfo({}); // エラー時も空にする
    }
  }, []); // API_URLは外部定数なので依存配列は空でOK

  useEffect(() => {
    const currentToken = localStorage.getItem("token");

    const fetchStudentData = async () => {
      if (!currentToken) {
        setLoadingStudents(false);
        setFetchError("ログインしていません。");
        setStudents([]);
        return;
      }
      setLoadingStudents(true);
      try {
        const res = await axios.get(`${API_URL}/teacher/logs/students/summary`, {
          headers: { Authorization: `Bearer ${currentToken}` },
        });
        setStudents(Array.isArray(res.data) ? res.data : []);
        setFetchError('');
      } catch (err) {
        console.error("学生サマリーの取得に失敗:", err);
        setFetchError('学生情報の取得に失敗しました。');
        setStudents([]);
      } finally {
        setLoadingStudents(false); 
      }
    };

    if (currentToken) {
        fetchStudentData();
        fetchActiveStudentData(); 
        
        const intervalId = setInterval(fetchActiveStudentData, 10000); // 10秒ごとに更新
        
        return () => clearInterval(intervalId);
    } else {
        setLoadingStudents(false);
        setFetchError("ログインしていません。");
        setStudents([]);
        setActiveStudentsInfo({});
    }
  }, [fetchActiveStudentData]);

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(field);
      setOrder("asc");
    }
  };

  const sortedStudents = useMemo(() => {
    return [...students].sort((a, b) => {
      let valA = a[orderBy];
      let valB = b[orderBy];
      if (orderBy === "total_watch_time" || orderBy === "videos_watched_count" || orderBy === "student_id") {
        valA = Number(valA) || 0;
        valB = Number(valB) || 0;
        return order === "asc" ? valA - valB : valB - valA;
      }
      valA = String(valA || ''); // null や undefined の場合にエラーにならないように
      valB = String(valB || '');
      return order === "asc"
        ? valA.localeCompare(valB, 'ja')
        : valB.localeCompare(valA, 'ja');
    });
  }, [students, orderBy, order]);

  const pageBgColor = theme.palette.mode === 'dark' ? '#0A0A0A' : theme.palette.background.default;
  const paperBgColor = theme.palette.mode === 'dark' ? '#121212' : theme.palette.background.paper;
  const textColor = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.87)' : theme.palette.text.primary;
  const tableHeaderBgColor = theme.palette.mode === 'dark' ? '#000000' : theme.palette.grey[200];
  const tableCellBorderColor = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)';
  const tableHeaderTextColor = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : theme.palette.text.secondary;
  const activeIconColor = theme.palette.mode === 'dark' ? '#66BB6A' : theme.palette.success.main;

  if (loadingStudents && students.length === 0) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress size={50} /></Box>;
  }

  if (fetchError && students.length === 0) {
    return <Typography color="error" align="center" sx={{mt: 3}}>{fetchError}</Typography>;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4, backgroundColor: pageBgColor }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, backgroundColor: paperBgColor, color: textColor }}>
        <Typography variant="h5" gutterBottom component="h1" sx={{ mb: 2, fontWeight: 'bold', color: textColor }}>
          学生一覧・視聴状況サマリー
        </Typography>
        {fetchError && students.length > 0 && (
            <Typography color="error" sx={{ mb: 2 }}>{fetchError}</Typography>
        )}
        {students.length === 0 && !loadingStudents && !fetchError && (
          <Typography sx={{ textAlign: 'center', py: 3, color: textColor }}>登録されている学生情報はありません。</Typography>
        )}
        {students.length > 0 && (
          <TableContainer component={Paper} sx={{backgroundColor: theme.palette.mode === 'dark' ? '#1E1E1E' : '#fff' }}>
            <Table sx={{ '& th, & td': { color: textColor, borderColor: tableCellBorderColor} }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: tableHeaderBgColor }}>
                  {[
                    { id: "student_id", label: "学生ID", numeric: true },
                    { id: "student_full_name", label: "氏名", numeric: false },
                    { id: "currently_watching", label: "視聴中動画", numeric: false, minWidth: 170 }, 
                    { id: "total_watch_time", label: "合計視聴時間", numeric: true },
                    { id: "videos_watched_count", label: "視聴動画数", numeric: true },
                  ].map((col) => (
                    <TableCell key={col.id} align={col.numeric ? "right" : "left"} sx={{ fontWeight: 'bold', minWidth: col.minWidth, color: tableHeaderTextColor }}>
                      <TableSortLabel
                        active={orderBy === col.id}
                        direction={orderBy === col.id ? order : "asc"}
                        onClick={() => handleSort(col.id)}
                        sx={{ '& .MuiTableSortLabel-icon': { color: `${tableHeaderTextColor} !important` } }}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedStudents.map((s) => {
                  const isActive = activeStudentsInfo.hasOwnProperty(String(s.student_id));
                  const watchingTitle = isActive ? activeStudentsInfo[String(s.student_id)] : "---";
                  // ★デバッグ用ログ追加
                  // if (students.length > 0 && Object.keys(activeStudentsInfo).length > 0) { // 条件をつけてログが大量に出ないように
                  //   console.log(`Student ID: ${s.student_id}, isActive: ${isActive}, watchingTitle: ${watchingTitle}, activeInfoForStudent: ${activeStudentsInfo[String(s.student_id)]}`);
                  // }


                  return (
                    <TableRow
                      key={s.student_id}
                      hover
                      onClick={() =>
                        navigate(`/teacher/student/${s.student_id}/history`)
                      }
                      sx={{ 
                        cursor: "pointer",
                        ...(isActive && { 
                            backgroundColor: ACTIVE_STUDENT_HIGHLIGHT_COLOR, 
                            '& td, & th': { 
                                color: `${theme.palette.getContrastText(ACTIVE_STUDENT_HIGHLIGHT_COLOR)} !important`,
                                fontWeight: 'bold',
                            },
                         })
                      }}
                    >
                      <TableCell align="right">{s.student_id}</TableCell>
                      <TableCell>
                        {isActive && (
                            <Tooltip title="現在アクティブ（視聴中の可能性が高いです）">
                                <FiberManualRecordIcon sx={{ fontSize: 12, color: activeIconColor, verticalAlign: 'middle', mr: 0.5 }} />
                            </Tooltip>
                        )}
                        {s.student_full_name}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={watchingTitle} placement="top-start">
                           <Typography variant="body2" component="span" sx={{...truncateTextStyle, color: isActive ? theme.palette.getContrastText(ACTIVE_STUDENT_HIGHLIGHT_COLOR) : 'inherit' }}>
                             {watchingTitle}
                           </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">{formatTime(s.total_watch_time)}</TableCell>
                      <TableCell align="right">{s.videos_watched_count}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Container>
  );
}